import React from "react";
import ReactToggle from "../../../../common/components/UI/ToggleSwitch/ReactToggle";
import { SearchableSelect } from "../../../../common/components/UI/Select";
import {
  getVtoCoverageRequiredEdgeNames,
  VtoCoverageRequiredEdge,
  RequestTypes,
  RequestSubtypes,
  VoluntaryTimeOffType,
} from "../../../Enums";
import { VTO_TYPES } from "../../../constants";
import i18n from '../../../../i18n';


/**
 * @typedef {typeof VtoCoverageRequiredEdge} IVtoCoverageRequiredEdge
 * @typedef {{ label: string; value: string|number }} ListItem
 * @typedef {typeof RequestTypes} IRequestType
 * @typedef {typeof RequestSubtypes} IRequestSubtype
 */

/**
 * @typedef {Omit<import('../../../typings/admin/adminSettingsReporting').AdminSettingsReporting, "requestSubtype">} IAdminSettingsReporting
 *
 * @typedef {Object} FormAdminSettingsStateProps
 * @property {ListItem?} requestSubtype
 * @property {boolean} isShowTimeOffRequestWarning Controls if the modal of `TimeOffRequestWarning` should be displayed on screen.
 *
 * @typedef {IAdminSettingsReporting & FormAdminSettingsStateProps} FormStateProps
 */

/**
 * @type {{
 *   value: IVtoCoverageRequiredEdge[keyof IVtoCoverageRequiredEdge];
 *   label: string;
 * }}
 */
export const vtoCoverageRequiredEdgesOptions = Object.values(
  VtoCoverageRequiredEdge
).map((option) => ({
  value: option,
  label: getVtoCoverageRequiredEdgeNames(option),
}));

/** @type {{[k: string]: import('react').CSSProperties}} */
export const styles = {
  NOOVERFLOW: {
    overflow: "hidden !important",
  },
  textField: {
    padding: "13px",
  },
  numberContainer: {
    display: "flex",
    alignItems: "center",
    padding: "15px 0px",
  },
  formContainer: {
    padding: "15px",
    zIndex: "1",
  },
  label: {
    width: "160px",
  },
  HeaderForm: {
    color: "#767676",
    borderBottom: "1px solid #E6EDF5",
    margin: "-15px",
    padding: "15px",
    backgroundColor: "white",
  },
  title: {
    fontSize: "17px",
    border: "1px solid #E6EDF5",
    width: "fit-content",
    padding: "10px",
    borderRadius: "25px",
    display: "inline-flex",
    fontWeight: 400,
  },
  cards: {
    marginTop: "5%",
    boxShadow: "none",
    border: "1px solid #E6EDF5",
    overflow: "visible",
  },
  addIcon: {
    color: "#6EA0D7",
  },
  cardHeader: {
    borderBottom: "1px solid #e6edf5",
  },
  containerOverlappingTc: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    marginTop: "9px",
  },
  isBlockOsToggleContainer: {
    display: "flex",
    flexDirection: "column",
    paddingTop: "2px",
    height: "41px",
    "& small": {
      marginTop: "-10px",
    },
  },
  minutesToBlockOs: {
    backgroundColor: "#cfc6c6",
  },
  numberPicker: {
    color: "#989898",
    height: "41px",
    lineHeight: "41px",
    minWidth: 50,
    border: "1px solid #E6EDF5",
    borderColor: "#E6EDF5",
    padding: 0,
  },
  numberPickers: {
    "& b": {
      background: "#FFF!important",
      border: "0!important",
      boxShadow: "none!important",
      right: "8px!important",
      width: "10px!important",
      top: "10px!important",
      cursor: "pointer!important",
    },
    "& b:last-child": {
      top: "inherit!important",
      bottom: "17px!important",
    },
    "& .react-numeric-input > input":{
      maxWidth: '100px'
    }
  },
  avatar: {
    color: "#9DBAD9",
    backgroundColor: "#E6EDF5",
    width: "30px",
    height: "30px",
    fontSize: "smaller",
  },
  maxLength: {
    color: "#A9A9A9",
    fontSize: "10px",
    textAlign: "left",
  },
  formControlLabel: {
    fontSize: 12,
    color: "grey",
  },
  requestSubtypeInput: {
    marginTop: "20px",
  },
  hoursMinutesContainer: {
    marginTop: 35,
    display: "block",
    "& b": {
      background: "#FFF!important",
      border: "0!important",
      boxShadow: "none!important",
      right: "8px!important",
      width: "10px!important",
      top: "10px!important",
      cursor: "pointer!important",
    },
    "& b:last-child": {
      top: "inherit!important",
      bottom: "17px!important",
    },
  },
  classLabel: {
    marginTop: -22,
    position: "absolute",
    color: "#878787",
    fontSize: 12,
  },
  paddingCard: {
    marginTop: "5%",
    paddingLeft: "10px",
    paddingRight: "10px",
    paddingBottom: "10px",
  },
  note: {
    color: "#A9A9A9",
    fontSize: "10px",
    textAlign: "left",
  },
  formContainerReadOnly: {
    padding: "15px",
    pointerEvents: "none",
  },
  privateEmployeeSourceContainer: {
    display: "flex",
  },
  privateEmployeeSourceContainerIcon: {
    alignSelf: "center",
    marginBottom: "34px",
    marginLeft: "16px",
  },
  vtoCheckBox:{
    marginBottom: "0px"
  }
};

export const HoursMinutesPickerStyles = {
  input: {
    color: "#989898",
    height: "41px",
    lineHeight: "41px",
    minWidth: 50,
    border: "1px solid #E6EDF5",
    borderColor: "#E6EDF5",
  },
  arrowUp: {
    background: "#FFF",
    borderBottomColor: "#6EA0D7",
  },
  arrowDown: {
    background: "transparent",
    borderTopColor: "#6EA0D7",
  },
  btn: {
    background: "#FFF",
    border: 0,
    boxShadow: "none",
    right: 8,
    width: 10,
    cursor: "pointer",
  },
  "btn:hover": {
    background: "#FFF",
  },
  "btn:active": {
    background: "#FFF",
    boxShadow: "none",
  },
  btnUp: {
    top: 10,
  },
  btnDown: {
    top: "inherit",
    bottom: 17,
  },
  inputNumber: {
    width: 20,
  },
};

export const generateToggle = (
  ToggleFct,
  value,
  labelOn,
  labelOff,
  note,
  hidden,
  key,
  disabled = false
) => (
  <ReactToggle
    ToggleFct={(checked) => ToggleFct(checked)}
    labelOn={labelOn}
    labelOff={labelOff}
    note={note}
    toggleValue={value}
    hidden={hidden}
    disabled={disabled}
    key={`${key}_${value}`}
  />
);

export const generateInputs = (ChangeFct, labelTypo, placeholder, value, list, target, disabled = false, note) => (
  <div>
    {labelTypo && <div className="labelWrapper"><span style={{ margin: 0, fontFamily: '"SF Pro Display","Roboto","Helvetica","Arial",sans-serif', fontWeight: 400, fontSize: '1rem', lineHeight: 1.5, letterSpacing: '0.00938em' }}>{labelTypo}</span></div>}
    <SearchableSelect
      options={list ?? []}
      value={value?.value !== undefined ? value : null}
      onChange={(selected) => ChangeFct(selected, target)}
      placeholder={placeholder}
      disabled={disabled}
    />
    {note && <span style={{ color: '#A9A9A9', fontSize: '0.75rem', textAlign: 'left', display: 'block' }}>{note}</span>}
  </div>
)

export const RESET_ADMIN_SETTING_FORM_VALUES = {
  breRuleSet: "",
  enablePartialShifts: false,
  multiplePartial: false,
  isAllowPartialOfPartial: false,
  minimumPartialShiftBalanceDuration: 0,
  minimumPartialShiftDuration: 0,
  isPartialOnlyOnEdges: false,
  vtoTypes: VTO_TYPES,
  maxAllowedPartial: null,
  isKeepEmployeeWorkRule: false,
  minimumShiftBalanceDuration: 0,
};

/**
 *
 * @param {*[]} breRuleSetList
 * @param {IRequestType} requestType
 * @param {bool} isFilterOutDoubleShiftsRule
 * @param {IRequestSubtype?} requestSubtype
 */
export const getBreRuleSetListOptions = (
  breRuleSetList,
  requestType,
  isFilterOutDoubleShiftsRule,
  requestSubtype = null
) => {
  const activeRuleSets = breRuleSetList.filter(
    (x) => x.isActive || x.id === null
  );
  switch (requestType) {
    case RequestTypes.openShift:
    case RequestTypes.voluntaryTimeOff:
      return activeRuleSets.filter(
        (r) =>
          (r.type === requestType &&
            r.requestSubtype === requestSubtype?.value) ||
          r.id === null
      );
    case RequestTypes.toCover:
    case RequestTypes.shiftSwap:
      return activeRuleSets.filter(
        (r) => (r.type === requestType || r.id === null) && (!isFilterOutDoubleShiftsRule || isFilterOutDoubleShiftsRule && !r.isMultipleShiftOnSameDayRuleEnabled)
      );
    default:
      return activeRuleSets.filter(
        (r) => r.type === requestType || r.id === null
      );
  }
};

/**
 * @param {*[]} vtoTypes
 * @param {IRequestSubtype?} requestSubtype
 */
export const getVtoTypes = (vtoTypes, requestSubtype = null) => {
  if (requestSubtype?.value === RequestSubtypes.fill) {
    return vtoTypes.filter((x) => x.value !== VoluntaryTimeOffType.BlockOfDays);
  }
  return vtoTypes;
};

export const getIsAllowProposalWithOfferTooltipOnDisabled = (isDisabledEdit, isMultipleShiftOnSameDayRuleAndGroupedEnabled, isKeepEmployeeWorkRuleAndGroupedEnabled) => {
  let tooltip = "";
  if (isDisabledEdit){
    tooltip += `${i18n.t('employmentBillboard:form.isAllowProposalWithOfferTooltip.isDisabledEdit')}\n`;
  }
  if (isMultipleShiftOnSameDayRuleAndGroupedEnabled){
    tooltip += `${i18n.t('employmentBillboard:form.isAllowProposalWithOfferTooltip.isMultipleShiftOnSameDayRuleAndGroupedEnabled')}\n`;
  }
  if (isKeepEmployeeWorkRuleAndGroupedEnabled){
    tooltip += `${i18n.t('employmentBillboard:form.isAllowProposalWithOfferTooltip.isKeepEmployeeWorkRuleAndGroupedEnabled')}\n`;
  }
  return tooltip;
}

export const getIsAllowMultipleRequestsTooltipOnDisabled = (isMultipleShiftOnSameDayRuleEnabled, isKeepEmployeeWorkRule) => {
  let tooltip = "";
  if (isMultipleShiftOnSameDayRuleEnabled) {
    tooltip += `${i18n.t('employmentBillboard:form.isAllowMultipleRequests.tooltip.isMultipleShiftOnSameDayRuleEnabled')}\n`;
  }
  if (isKeepEmployeeWorkRule) {
    tooltip += `${i18n.t('employmentBillboard:form.isAllowMultipleRequests.tooltip.isKeepEmployeeWorkRule')}\n`;
  }
  return tooltip;
}

export const getGroupedShiftSwapTooltipOnDisabled = (isDisabledEdit,  isAllowProposalWithOffer,  isMultipleShiftOnSameDayRuleEnabled, isKeepEmployeeWorkRule) => {
  let tooltip = "";
  if (isDisabledEdit){
    tooltip += `${i18n.t('employmentBillboard:form.groupedShiftSwapTooltip.isDisabledEdit')}\n`;
  }
  if (isAllowProposalWithOffer){
    tooltip += `${i18n.t('employmentBillboard:form.groupedShiftSwapTooltip.isAllowProposalWithOffer')}\n`;
  }
  if (isMultipleShiftOnSameDayRuleEnabled){
    tooltip += `${i18n.t('employmentBillboard:form.groupedShiftSwapTooltip.isMultipleShiftOnSameDayRuleEnabled')}\n`;
  }
  if (isKeepEmployeeWorkRule){
    tooltip += `${i18n.t('employmentBillboard:form.groupedShiftSwapTooltip.isKeepEmployeeWorkRule')}\n`;
  }
  return tooltip;
}

export const getIsKeepEmployeeWorkRuleTooltipOnDisabled = (isAllowMultipleRequestsAndGroupedEnabled) => {
  if (isAllowMultipleRequestsAndGroupedEnabled){
    return i18n.t('employmentBillboard:form.isKeepEmployeeWorkRuleTooltip.isAllowMultipleRequestsAndGroupedEnabled');
  }
}

export const getEnableShiftSwapEmployeePreferencesTooltipOnDisabled = (isDisabledEdit, isAllowProposalWithOffer) => {
  let tooltip = "";
  if (isDisabledEdit){
    tooltip += `${i18n.t('employmentBillboard:form.isShiftSwapEmployeePreferencesTitleTooltip.isDisabledEdit')}\n`;
  }
  if (isAllowProposalWithOffer){
    tooltip += `${i18n.t('employmentBillboard:form.isShiftSwapEmployeePreferencesTitleTooltip.isAllowProposalWithOffer')}\n`;
  }
  return tooltip;
}

